import Microgame from '../engine/Microgame.js';

export default class SkateGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Skate !`, en: `Skate!`, es: `¡Monopatín!` },
            controls: { fr: `Appuie pour sauter (Ollie) par-dessus les obstacles`, en: `Tap to jump (Ollie) over obstacles`, es: `Toca para saltar (Ollie) sobre los obstáculos` },
            objective: { fr: `Évite toutes les poubelles jusqu'à la fin de la rue`, en: `Avoid all the trash cans until the end of the street`, es: `Evita todos los cubos de basura hasta el final de la calle` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="M12 12A5 5 0 1 0 12 2a5 5 0 0 0 0 10Z"/><path d="M13 12h-2v10"/><path d="M15 14H9"/><path d="m19 19-3-5"/><path d="m5 19 3-5"/></svg>`,
            actions: [
                { type: 'wait', duration: 1320 },
                { type: 'tap', x: 20, y: 76, duration: 180 },
                { type: 'wait', duration: 900 },
            ],
            loopDuration: 3200
        };
    }
    constructor(engine) {
        super(engine);
        this.skater = null;
        this.obstacle = null;
        this.isJumping = false;
        this.isCrouching = false;
        this.obstacleX = 400;
        this.groundY = 0;

        const speedMult = engine.speedMultiplier || 1.0;
        
        // Scale down maximum speed so that x4 feels exactly like x3.1
        const effectiveMult = 1.0 + (speedMult - 1) * 0.7;

        // Override GameEngine division so game duration feels exactly like effectiveMult
        this.duration = 4000 * (speedMult / effectiveMult);
        
        this.baseObstacleSpeed = 5 * effectiveMult; // 5 at x1, 15.5 at x4 (old 3.1)
        this.jumpDuration = 800 / effectiveMult; // 800ms at x1, ~258ms at x4
    }

    setup() {
        this.engine.ui.instruction.textContent = "JUMP!";

        const W = this.container.clientWidth || 400;
        const H = this.container.clientHeight || 700;
        this.groundY = H * 0.72;

        // Scale true speed by screen width so it always reaches the player in time (based on 400px ref width)
        this.obstacleSpeed = this.baseObstacleSpeed * (W / 400);

        // Background: subtle vertical gradient using primary
        const bg = document.createElement('div');
        bg.style.position = 'absolute';
        bg.style.width = '100%';
        bg.style.height = '100%';
        bg.style.background = `linear-gradient(180deg, ${this.darken(this.primaryColor, 0.08)} 0%, ${this.darken(this.primaryColor, 0.18)} 100%)`;
        this.container.appendChild(bg);

        // Ground line
        const ground = document.createElement('div');
        ground.style.position = 'absolute';
        ground.style.width = '100%';
        ground.style.height = '3px';
        ground.style.background = this.secondaryColor;
        ground.style.top = `${this.groundY}px`;
        ground.style.boxShadow = `0 0 12px ${this.secondaryColor}66`;
        this.container.appendChild(ground);

        // Ground fill below the line
        const groundFill = document.createElement('div');
        groundFill.style.position = 'absolute';
        groundFill.style.width = '100%';
        groundFill.style.top = `${this.groundY + 3}px`;
        groundFill.style.bottom = '0';
        groundFill.style.background = `${this.secondaryColor}15`;
        this.container.appendChild(groundFill);

        // Speed lines (decorative, below ground)
        for (let i = 0; i < 4; i++) {
            const line = document.createElement('div');
            line.style.position = 'absolute';
            line.style.width = `${30 + Math.random() * 60}px`;
            line.style.height = '1px';
            line.style.background = `${this.secondaryColor}33`;
            line.style.top = `${this.groundY + 15 + Math.random() * 60}px`;
            line.style.left = `${Math.random() * 100}%`;
            this.container.appendChild(line);

            const speedMult = this.engine.speedMultiplier || 1.0;
            const speedScale = 1 + (speedMult - 1) * 0.55;
            // Animate scrolling left (scale speed visually)
            line.animate([
                { transform: 'translateX(0)' },
                { transform: `translateX(-${W + 100}px)` }
            ], { duration: (1800 + Math.random() * 1000) / speedScale, iterations: Infinity });
        }

        // Skater container
        this.skater = document.createElement('div');
        this.skater.style.position = 'absolute';
        this.skater.style.width = '44px';
        this.skater.style.height = '70px';
        this.skater.style.bottom = `${H - this.groundY}px`;
        this.skater.style.left = `${W * 0.15}px`;
        this.skater.style.zIndex = '3';
        this.skater.style.transformOrigin = 'bottom center';
        this.container.appendChild(this.skater);

        // Head
        const head = document.createElement('div');
        head.style.position = 'absolute';
        head.style.width = '18px';
        head.style.height = '18px';
        head.style.background = this.primaryColor;
        head.style.borderRadius = '50%';
        head.style.top = '0';
        head.style.left = '50%';
        head.style.transform = 'translateX(-50%)';
        head.style.boxShadow = `0 0 8px ${this.primaryColor}88`;
        this.skater.appendChild(head);

        // Body
        const body = document.createElement('div');
        body.style.position = 'absolute';
        body.style.width = '14px';
        body.style.height = '28px';
        body.style.background = this.primaryColor;
        body.style.borderRadius = '4px';
        body.style.top = '16px';
        body.style.left = '50%';
        body.style.transform = 'translateX(-50%)';
        this.skater.appendChild(body);

        // Board
        const board = document.createElement('div');
        board.style.position = 'absolute';
        board.style.width = '44px';
        board.style.height = '6px';
        board.style.background = this.secondaryColor;
        board.style.borderRadius = '3px';
        board.style.bottom = '6px';
        board.style.left = '0';
        board.style.boxShadow = `0 0 6px ${this.secondaryColor}66`;
        this.skater.appendChild(board);

        // Wheels
        for (let wx of [6, 32]) {
            const wheel = document.createElement('div');
            wheel.style.position = 'absolute';
            wheel.style.width = '8px';
            wheel.style.height = '8px';
            wheel.style.background = this.darken(this.secondaryColor, 0.5);
            wheel.style.borderRadius = '50%';
            wheel.style.bottom = '0';
            wheel.style.left = `${wx}px`;
            this.skater.appendChild(wheel);
        }

        // Obstacle: randomize type (cone, barrel, rail)
        const obstacleTypes = ['cone', 'barrel', 'rail'];
        this.obsType = this.isPlayback ? 'cone' : obstacleTypes[Math.floor(Math.random() * obstacleTypes.length)];

        this.obstacle = document.createElement('div');
        this.obstacle.style.position = 'absolute';
        this.obstacle.style.bottom = `${H - this.groundY}px`;
        this.obstacle.style.zIndex = '2';

        if (this.obsType === 'cone') {
            this.obstacle.style.width = '0';
            this.obstacle.style.height = '0';
            this.obstacle.style.borderLeft = '16px solid transparent';
            this.obstacle.style.borderRight = '16px solid transparent';
            this.obstacle.style.borderBottom = `40px solid ${this.secondaryColor}`;
            this.obstacle.style.filter = `drop-shadow(0 0 6px ${this.secondaryColor}66)`;
        } else if (this.obsType === 'barrel') {
            this.obstacle.style.width = '34px';
            this.obstacle.style.height = '38px';
            this.obstacle.style.background = `linear-gradient(90deg, ${this.darken(this.secondaryColor, 0.6)}, ${this.secondaryColor}, ${this.darken(this.secondaryColor, 0.6)})`;
            this.obstacle.style.borderRadius = '6px';
            this.obstacle.style.boxShadow = `0 0 8px ${this.secondaryColor}44`;

            // Barrel stripe
            const stripe = document.createElement('div');
            stripe.style.position = 'absolute';
            stripe.style.width = '100%';
            stripe.style.height = '4px';
            stripe.style.background = `${this.primaryColor}66`;
            stripe.style.top = '50%';
            stripe.style.transform = 'translateY(-50%)';
            this.obstacle.appendChild(stripe);
        } else {
            // Rail
            this.obstacle.style.width = '50px';
            this.obstacle.style.height = '30px';
            this.obstacle.style.borderTop = `4px solid ${this.secondaryColor}`;
            this.obstacle.style.borderLeft = `3px solid ${this.secondaryColor}99`;
            this.obstacle.style.borderRight = `3px solid ${this.secondaryColor}99`;
            this.obstacle.style.boxShadow = `0 0 8px ${this.secondaryColor}33`;
        }

        // Randomize the start distance vastly so it's never exactly the same timing, but safe
        this.obstacleX = this.isPlayback ? (W + 30) : (W + 40 + Math.random() * (W * 0.6));
        this.obstacle.style.left = `${this.obstacleX}px`;
        this.container.appendChild(this.obstacle);

        // Warning indicator (exclamation)
        this.warning = document.createElement('div');
        this.warning.style.position = 'absolute';
        this.warning.style.top = `${this.groundY - 90}px`;
        this.warning.style.right = '15px';
        this.warning.style.fontSize = '28px';
        this.warning.style.color = this.primaryColor;
        this.warning.style.fontWeight = 'bold';
        this.warning.style.opacity = '0';
        this.warning.style.transition = 'opacity 0.2s';
        this.warning.textContent = '!';
        this.container.appendChild(this.warning);

        this.onDown = this.onDown.bind(this);
        this.onUp = this.onUp.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointerup', this.onUp);

        this.animate();
    }

    darken(hex, factor) {
        const ctx = document.createElement('canvas').getContext('2d');
        ctx.fillStyle = hex;
        const match = ctx.fillStyle.match(/\d+/g);
        if (!match) return hex;
        return `rgb(${Math.floor(match[0] * factor)},${Math.floor(match[1] * factor)},${Math.floor(match[2] * factor)})`;
    }

    animate() {
        if (!this.active) return;

        // Move obstacle left
        this.obstacleX -= this.obstacleSpeed;
        this.obstacle.style.left = `${this.obstacleX}px`;

        // Warning flash when obstacle approaches
        const W = this.container.clientWidth || 400;
        if (this.obstacleX < W * 0.7 && this.obstacleX > W * 0.3) {
            this.warning.style.opacity = '1';
        } else {
            this.warning.style.opacity = '0';
        }

        // Collision Check
        const skaterRect = this.skater.getBoundingClientRect();
        const obsRect = this.obstacle.getBoundingClientRect();

        if (skaterRect.right > obsRect.left + 8 &&
            skaterRect.left < obsRect.right - 8 &&
            skaterRect.bottom > obsRect.top + 8) {
            this.lose();
            // Crash animation
            this.skater.style.transition = 'transform 0.3s';
            this.skater.style.transform = 'rotate(70deg) translateX(20px)';
            this.skater.style.opacity = '0.5';
        }

        // Win: obstacle fully passed
        if (this.obstacleX < -60) {
            this.win();
        }

        requestAnimationFrame(() => this.animate());
    }

    onDown() {
        if (this.isJumping || this.won || this.lost) return;
        this.isCrouching = true;
        this.skater.style.transition = 'transform 0.1s ease-out';
        this.skater.style.transform = 'scaleY(0.6) scaleX(1.3)';
    }

    onUp() {
        if (!this.isCrouching || this.isJumping || this.won || this.lost) return;
        this.isCrouching = false;
        this.isJumping = true;

        this.skater.style.transition = 'none';
        this.skater.style.transform = 'scaleY(1) scaleX(1)'; // reset base for animation

        const H = this.container.clientHeight || 700;
        const groundBottom = H - this.groundY;

        // Jump starting from crouched state (snappy takeoff using per-keyframe easing)
        this.skater.animate([
            { bottom: `${groundBottom}px`, transform: 'scaleY(0.6) scaleX(1.3)', easing: 'ease-out' }, // Launch fast!
            { bottom: `${groundBottom + 85}px`, transform: 'scaleY(1.15) scaleX(0.9)', offset: 0.45, easing: 'ease-in-out' },
            { bottom: `${groundBottom + 85}px`, transform: 'scaleY(1.05) scaleX(0.95)', offset: 0.55, easing: 'ease-in' }, // Fall fast!
            { bottom: `${groundBottom}px`, transform: 'scaleY(0.8) scaleX(1.15)', offset: 0.92, easing: 'ease-out' }, // Landing impact
            { bottom: `${groundBottom}px`, transform: 'scaleY(1) scaleX(1)' }
        ], {
            duration: this.jumpDuration
        }).onfinish = () => {
            this.isJumping = false;
            // If pointer is held down during landing, crouch immediately
            if (this.isCrouching) {
                this.skater.style.transition = 'transform 0.1s ease-out';
                this.skater.style.transform = 'scaleY(0.6) scaleX(1.3)';
            }
        };
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
